import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { medicationAPI, hipaaAPI } from '../../lib/api';
import { 
  Pill,
  Camera,
  Upload,
  CheckCircle,
  Warning,
  Spinner,
  FileText,
  Trash,
  Eye,
  Plus,
  MagnifyingGlass,
  ShieldCheck,
  ArrowRight
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function MedicationsPage() {
  const { user } = useAuth();
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState('');
  const [imageType, setImageType] = useState('pharmacy_receipt');
  const [extractionResult, setExtractionResult] = useState(null);
  const [hasConsent, setHasConsent] = useState(false);
  const [consentLoading, setConsentLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [newMed, setNewMed] = useState({
    medication_name: '',
    dosage: '',
    frequency: '',
    route: 'oral',
    prescriber_name: '',
    pharmacy_name: '',
    instructions: '',
    reason: ''
  });

  const loadData = useCallback(async () => {
    try {
      const response = await medicationAPI.getPatientMeds(user.id, false);
      setMedications(response.data.medications || []);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error loading medications:', error);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  const checkConsent = useCallback(async () => {
    try {
      const statusRes = await hipaaAPI.getStatus();
      setHasConsent(statusRes.data.medication_records_release || false);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error checking consent:', error);
    } finally {
      setConsentLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    checkConsent();
  }, [loadData, checkConsent]);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImagePreview(reader.result);
      setImageBase64(reader.result.split(',')[1]);
    };
    reader.readAsDataURL(file);
  };

  const handleExtract = async () => {
    if (!imageBase64) {
      toast.error('Please upload an image first');
      return;
    }

    if (!hasConsent) {
      toast.error('Please sign the Medication Records Release consent form first');
      return;
    }

    setExtracting(true);
    try {
      const response = await medicationAPI.extractFromImage({
        patient_id: user.id,
        image_base64: imageBase64,
        image_type: imageType
      });

      if (response.data.success) {
        setExtractionResult(response.data);
        toast.success(`Extracted ${response.data.medications_added} medication(s)!`);
        loadData(); // Refresh the list
      } else {
        toast.error('Could not extract medications. Please try again or add manually.');
      }
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Extraction error:', error);
      if (error.response?.status === 403) {
        toast.error('Please sign the Medication Records Release consent form first');
      } else {
        toast.error('Failed to extract medications');
      }
    } finally {
      setExtracting(false);
    }
  };

  const handleAddMedication = async (e) => {
    e.preventDefault();
    if (!newMed.medication_name || !newMed.dosage || !newMed.frequency) {
      toast.error('Please fill in medication name, dosage, and frequency');
      return;
    }

    try {
      await medicationAPI.add({
        patient_id: user.id,
        ...newMed
      });
      toast.success('Medication added successfully');
      setShowAddModal(false);
      setNewMed({
        medication_name: '',
        dosage: '',
        frequency: '',
        route: 'oral',
        prescriber_name: '',
        pharmacy_name: '',
        instructions: '',
        reason: ''
      });
      loadData();
    } catch (error) {
      toast.error('Failed to add medication');
    }
  };

  const handleDeleteMedication = async (id) => {
    if (!window.confirm('Are you sure you want to discontinue this medication?')) return;
    
    try {
      await medicationAPI.delete(id);
      toast.success('Medication discontinued');
      loadData();
    } catch (error) {
      toast.error('Failed to discontinue medication');
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await medicationAPI.search(query);
      setSearchResults(response.data.results || []);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  const selectMedication = (med) => {
    setNewMed({
      ...newMed,
      medication_name: med.name
    });
    setSearchQuery(med.name);
    setSearchResults([]);
  };

  const activeMeds = medications.filter(m => m.status === 'active');
  const discontinuedMeds = medications.filter(m => m.status === 'discontinued');

  if (loading || consentLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size={32} className="animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
            My Medications
          </h1>
          <p className="text-slate-500 mt-1">Manage your current medications and prescriptions</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowUploadModal(true)}
            className="btn-secondary flex items-center gap-2"
            data-testid="upload-prescription-btn"
          >
            <Camera size={20} />
            Scan Prescription
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-teal flex items-center gap-2"
            data-testid="add-medication-btn"
          >
            <Plus size={20} />
            Add Medication
          </button>
        </div>
      </div>

      {/* Consent Warning */}
      {!hasConsent && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3" data-testid="consent-required-banner">
          <Warning size={24} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-amber-800">Medication Records Consent Required</h3>
            <p className="text-amber-700 text-sm mt-1">
              To use the AI prescription scanner, please sign the Medication Records Release consent form.
            </p>
            <a 
              href="/patient/hipaa" 
              className="inline-flex items-center gap-1 mt-2 text-sm font-medium text-amber-800 hover:text-amber-900"
            >
              Go to Consent Forms <ArrowRight size={16} />
            </a>
          </div>
        </div>
      )}

      {/* Active Medications */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Pill size={20} className="text-teal-600" />
          Active Medications ({activeMeds.length})
        </h2>
        
        {activeMeds.length === 0 ? (
          <div className="card-clinical p-8 text-center">
            <Pill size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Active Medications</h3>
            <p className="text-slate-500 mb-4">
              Add your current medications manually or scan a pharmacy receipt
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setShowUploadModal(true)}
                className="btn-secondary flex items-center gap-2"
              >
                <Camera size={18} />
                Scan Prescription
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="btn-teal flex items-center gap-2"
              >
                <Plus size={18} />
                Add Manually
              </button>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {activeMeds.map((med) => (
              <div 
                key={med.id} 
                className="card-clinical p-4"
                data-testid={`medication-card-${med.id}`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                      {med.medication_name}
                      {!med.verified && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                          Unverified
                        </span>
                      )}
                      {med.verified && (
                        <CheckCircle size={16} className="text-green-500" weight="fill" />
                      )}
                    </h3>
                    <p className="text-teal-600 font-medium">{med.dosage}</p>
                    <p className="text-slate-500 text-sm">{med.frequency}</p>
                  </div>
                  <button
                    onClick={() => handleDeleteMedication(med.id)}
                    className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                    title="Discontinue medication"
                  >
                    <Trash size={18} />
                  </button>
                </div>
                
                {(med.pharmacy_name || med.prescriber_name) && (
                  <div className="mt-3 pt-3 border-t border-slate-100 text-sm text-slate-500">
                    {med.prescriber_name && <p>Prescriber: {med.prescriber_name}</p>}
                    {med.pharmacy_name && <p>Pharmacy: {med.pharmacy_name}</p>}
                    {med.instructions && <p className="mt-1 italic">{med.instructions}</p>}
                  </div>
                )}
                
                {med.source === 'ai_extracted' && (
                  <div className="mt-2 text-xs text-slate-400">
                    Extracted from uploaded image
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Discontinued Medications */}
      {discontinuedMeds.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <FileText size={20} className="text-slate-400" />
            Discontinued Medications ({discontinuedMeds.length})
          </h2>
          <div className="grid md:grid-cols-2 gap-4 opacity-60">
            {discontinuedMeds.map((med) => (
              <div key={med.id} className="card-clinical p-4 bg-slate-50">
                <h3 className="font-semibold text-slate-600 line-through">{med.medication_name}</h3>
                <p className="text-slate-500">{med.dosage} - {med.frequency}</p>
                <p className="text-xs text-slate-400 mt-1">
                  Discontinued: {new Date(med.end_date).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload/Scan Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="upload-modal">
            <div className="p-6 border-b border-slate-100">
              <h2 className="text-xl font-bold text-slate-900">Scan Prescription / Medication</h2>
              <p className="text-slate-500 text-sm mt-1">
                Upload a photo and our AI will extract the medication information
              </p>
            </div>

            <div className="p-6 space-y-6">
              {/* Image Type Selection */}
              <div>
                <label className="label-clinical">What are you uploading?</label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {[
                    { value: 'pharmacy_receipt', label: 'Pharmacy Receipt' },
                    { value: 'medication_bottle', label: 'Medication Bottle' },
                    { value: 'prescription_label', label: 'Prescription Label' }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setImageType(type.value)}
                      className={`p-3 rounded-lg text-sm font-medium transition-all ${
                        imageType === type.value
                          ? 'bg-teal-500 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Upload Area */}
              <div>
                <label className="label-clinical">Upload Image</label>
                <div className="mt-2 border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-teal-300 transition-colors">
                  {imagePreview ? (
                    <div className="space-y-4">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="max-h-64 mx-auto rounded-lg"
                      />
                      <button
                        onClick={() => {
                          setImagePreview(null);
                          setImageBase64('');
                        }}
                        className="text-sm text-red-500 hover:text-red-600"
                      >
                        Remove image
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <Camera size={48} className="mx-auto text-slate-300 mb-3" />
                      <p className="text-slate-600 font-medium">Click to upload image</p>
                      <p className="text-slate-400 text-sm mt-1">JPG, PNG up to 10MB</p>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="hidden"
                        data-testid="image-upload-input"
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Consent Notice */}
              {!hasConsent && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                  <p className="text-amber-800 flex items-center gap-2">
                    <ShieldCheck size={18} />
                    You need to sign the Medication Records consent to use this feature.
                  </p>
                </div>
              )}

              {/* Extraction Result */}
              {extractionResult && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-semibold text-green-800 flex items-center gap-2">
                    <CheckCircle size={20} />
                    Extraction Complete
                  </h4>
                  <p className="text-green-700 text-sm mt-1">
                    {extractionResult.medications_added} medication(s) added to your list.
                    Please review and verify the information.
                  </p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-100 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setImagePreview(null);
                  setImageBase64('');
                  setExtractionResult(null);
                }}
                className="btn-secondary"
              >
                Close
              </button>
              <button
                onClick={handleExtract}
                disabled={!imageBase64 || extracting || !hasConsent}
                className="btn-teal flex items-center gap-2"
                data-testid="extract-medications-btn"
              >
                {extracting ? (
                  <>
                    <Spinner size={18} className="animate-spin" />
                    Extracting...
                  </>
                ) : (
                  <>
                    <MagnifyingGlass size={18} />
                    Extract Medications
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Medication Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="add-medication-modal">
            <div className="p-6 border-b border-slate-100">
              <h2 className="text-xl font-bold text-slate-900">Add Medication</h2>
              <p className="text-slate-500 text-sm mt-1">
                Enter your medication details
              </p>
            </div>

            <form onSubmit={handleAddMedication} className="p-6 space-y-4">
              {/* Search for medication */}
              <div className="relative">
                <label className="label-clinical">Medication Name *</label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery || newMed.medication_name}
                    onChange={(e) => {
                      setNewMed({ ...newMed, medication_name: e.target.value });
                      handleSearch(e.target.value);
                    }}
                    className="input-clinical pr-10"
                    placeholder="Start typing to search..."
                    required
                    data-testid="medication-name-input"
                  />
                  {searching && (
                    <Spinner size={18} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />
                  )}
                </div>
                
                {/* Search Results Dropdown */}
                {searchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {searchResults.map((med) => (
                      <button
                        key={`search-${med.name}-${med.class || ''}`}
                        type="button"
                        onClick={() => selectMedication(med)}
                        className="w-full px-4 py-2 text-left hover:bg-slate-50 flex justify-between items-center"
                      >
                        <span className="font-medium">{med.name}</span>
                        <span className="text-xs text-slate-500">{med.class}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-clinical">Dosage *</label>
                  <input
                    type="text"
                    value={newMed.dosage}
                    onChange={(e) => setNewMed({ ...newMed, dosage: e.target.value })}
                    className="input-clinical"
                    placeholder="e.g., 10mg"
                    required
                    data-testid="medication-dosage-input"
                  />
                </div>
                <div>
                  <label className="label-clinical">Frequency *</label>
                  <select
                    value={newMed.frequency}
                    onChange={(e) => setNewMed({ ...newMed, frequency: e.target.value })}
                    className="input-clinical"
                    required
                    data-testid="medication-frequency-select"
                  >
                    <option value="">Select frequency</option>
                    <option value="Once daily">Once daily</option>
                    <option value="Twice daily">Twice daily</option>
                    <option value="Three times daily">Three times daily</option>
                    <option value="Four times daily">Four times daily</option>
                    <option value="Every morning">Every morning</option>
                    <option value="Every night">Every night</option>
                    <option value="As needed">As needed</option>
                    <option value="Weekly">Weekly</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="label-clinical">Route</label>
                <select
                  value={newMed.route}
                  onChange={(e) => setNewMed({ ...newMed, route: e.target.value })}
                  className="input-clinical"
                >
                  <option value="oral">Oral (by mouth)</option>
                  <option value="topical">Topical (skin)</option>
                  <option value="injection">Injection</option>
                  <option value="inhaled">Inhaled</option>
                  <option value="sublingual">Sublingual (under tongue)</option>
                  <option value="rectal">Rectal</option>
                  <option value="ophthalmic">Eye drops</option>
                  <option value="otic">Ear drops</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-clinical">Prescriber Name</label>
                  <input
                    type="text"
                    value={newMed.prescriber_name}
                    onChange={(e) => setNewMed({ ...newMed, prescriber_name: e.target.value })}
                    className="input-clinical"
                    placeholder="Dr. Smith"
                  />
                </div>
                <div>
                  <label className="label-clinical">Pharmacy</label>
                  <input
                    type="text"
                    value={newMed.pharmacy_name}
                    onChange={(e) => setNewMed({ ...newMed, pharmacy_name: e.target.value })}
                    className="input-clinical"
                    placeholder="CVS, Walgreens, etc."
                  />
                </div>
              </div>

              <div>
                <label className="label-clinical">Instructions</label>
                <input
                  type="text"
                  value={newMed.instructions}
                  onChange={(e) => setNewMed({ ...newMed, instructions: e.target.value })}
                  className="input-clinical"
                  placeholder="Take with food, etc."
                />
              </div>

              <div>
                <label className="label-clinical">Reason for Taking</label>
                <input
                  type="text"
                  value={newMed.reason}
                  onChange={(e) => setNewMed({ ...newMed, reason: e.target.value })}
                  className="input-clinical"
                  placeholder="High blood pressure, diabetes, etc."
                />
              </div>

              <div className="pt-4 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-teal"
                  data-testid="submit-medication-btn"
                >
                  Add Medication
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
